/**
 * ریاضیات نمودار عمق بازار
 * ==========================
 * از دفتر سفارش، شکلِ «تا این قیمت چقدر حجم هست» را می‌سازد.
 *
 * دو نکته که اگر رعایت نشوند نمودار **دروغ** می‌گوید:
 *
 * ۱. تجمعی، نه تک‌تک. پرسش کاربر این است که «اگر الان بفروشم، قیمت تا کجا
 *    می‌آید؟» و پاسخش جمعِ همه سفارش‌ها تا آن قیمت است.
 * ۲. پله‌ای، نه اریب. بین دو سطحِ قیمت هیچ سفارشی وجود ندارد؛ خطِ اریب،
 *    نقدینگی‌ای را نشان می‌دهد که نیست.
 *
 * بدون هیچ وابستگی به مرورگر، تا مستقیم آزمون‌پذیر باشد.
 */
export type Level = [number, number]
export type Book = { bids: Level[]; asks: Level[]; last?: number }
export type Step = { price: number; cum: number; x: number; y: number }

export type DepthView = {
  bidLine: string; bidArea: string; askLine: string; askArea: string
  midX: number; mid: number; spread: number; spreadPct: number
  lo: number; hi: number; maxCum: number
  xTicks: { x: number; v: number }[]
  yTicks: { y: number; v: number }[]
  bids: Step[]; asks: Step[]
}

export const validLevel = (l: any): l is Level =>
  Array.isArray(l) && Number.isFinite(l[0]) && Number.isFinite(l[1]) && l[0] > 0 && l[1] > 0

/** جمع تجمعی روی سطح‌های از پیش مرتب‌شده */
export function cumulative(levels: Level[]): { price: number; cum: number }[] {
  let s = 0
  return levels.map(([p, a]) => ({ price: p, cum: (s += a) }))
}

/**
 * @param zoom بازه نمایش بر حسب درصدِ قیمت میانی؛ `Infinity` یعنی کل دفتر
 * @param size اندازه ناحیه رسم به پیکسل
 * @param pad جای برچسب‌های پایین
 */
export function depthView(
  book: Book | null | undefined,
  zoom: number,
  size: { w: number; h: number },
  pad = 18,
): DepthView | null {
  if (!book) return null
  const bids = (book.bids || []).filter(validLevel).slice().sort((a, b) => b[0] - a[0])
  const asks = (book.asks || []).filter(validLevel).slice().sort((a, b) => a[0] - b[0])
  if (!bids.length || !asks.length) return null

  const bestBid = bids[0][0]
  const bestAsk = asks[0][0]
  const mid = (bestBid + bestAsk) / 2
  const { w, h } = size
  if (!(mid > 0) || !(w > 0) || !(h > pad + 8)) return null

  // بازه، متقارن حول قیمت میانی است تا دو سمت با یک مقیاس دیده شوند.
  //
  // کفِ بازه هرگز از خودِ اختلاف خرید و فروش کمتر نمی‌شود. اگر می‌شد، در
  // بازارهای کم‌عمق (که اختلافشان از بزرگ‌نمایی انتخابی بیشتر است) حتی
  // بهترین سفارش هم بیرون از بازه می‌افتاد و نمودار برای همیشه «در حال
  // دریافت» می‌ماند — درحالی‌که داده کاملاً سالم بود.
  const spreadNow = bestAsk - bestBid
  const span = Number.isFinite(zoom)
    ? Math.max(mid * zoom, spreadNow)
    : Math.max(mid - bids[bids.length - 1][0], asks[asks.length - 1][0] - mid, spreadNow)
  const lo = mid - span
  const hi = mid + span
  if (!(hi > lo)) return null

  const X = (p: number) => ((p - lo) / (hi - lo)) * w
  const cb = cumulative(bids).filter((r) => r.price >= lo)
  const ca = cumulative(asks).filter((r) => r.price <= hi)
  if (!cb.length || !ca.length) return null

  const maxCum = Math.max(cb[cb.length - 1].cum, ca[ca.length - 1].cum) * 1.06
  const base = h - pad
  const Y = (v: number) => base - (v / maxCum) * (base - 8)

  const bids2: Step[] = cb.map((r) => ({ ...r, x: X(r.price), y: Y(r.cum) }))
  const asks2: Step[] = ca.map((r) => ({ ...r, x: X(r.price), y: Y(r.cum) }))
  const midX = X(mid)

  // پله: در هر سطح اول افقی تا قیمت، بعد عمودی تا حجم تازه
  const stair = (pts: Step[]) => {
    let d = `M${midX},${base}`
    let prevY = base
    for (const p of pts) { d += ` L${p.x},${prevY} L${p.x},${p.y}`; prevY = p.y }
    return d
  }
  const bidLine = stair(bids2)
  const askLine = stair(asks2)

  return {
    bidLine,
    bidArea: `${bidLine} L${bids2[bids2.length - 1].x},${base} Z`,
    askLine,
    askArea: `${askLine} L${asks2[asks2.length - 1].x},${base} Z`,
    midX, mid,
    spread: spreadNow,
    spreadPct: (spreadNow / mid) * 100,
    lo, hi, maxCum,
    xTicks: [0.08, 0.3, 0.7, 0.92].map((f) => ({ x: f * w, v: lo + (hi - lo) * f })),
    yTicks: [0.25, 0.5, 0.75, 1].map((f) => ({ y: Y(maxCum * f), v: maxCum * f })),
    bids: bids2, asks: asks2,
  }
}

/** نزدیک‌ترین پله به مکان نشانگر */
export function probeDepth(v: DepthView, px: number) {
  const side: 'bid' | 'ask' = px <= v.midX ? 'bid' : 'ask'
  const rows = side === 'bid' ? v.bids : v.asks
  if (!rows.length) return null
  let best = rows[0]
  for (const r of rows) if (Math.abs(r.x - px) < Math.abs(best.x - px)) best = r
  return {
    side, x: best.x, y: best.y, price: best.price,
    amount: best.cum, value: best.cum * best.price,
  }
}
